import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  Card,
  Column,
  Columns,
  Divider,
  Heading,
  Radio,
  RadioGroup,
  Stack,
} from "@origin-digital/ods-core";
import { Field, Formik, FormikProps, yupToFormErrors } from "formik";
import {
  ClaimDialogValues,
  claimFormInitialValues,
  ClaimFormValues,
} from "./types";
import { AccountPayer, ErrorTypeEnum } from "../types";
import { IFormFunctionProps } from "../FormAccordionContainer";
import ContactField from "../FormComponents/ContactField";
import { claimValuesToRestMapper } from "./helper";
import { useNavigate } from "react-router-dom";
import { claimFormValidationSchema } from "./validations";
import { apiClient } from "src/api/apiService";
import ClaimsAccountBlock from "../ClaimsAccountBlock";
import { accountBlockInitialValues } from "../FormComponents/Validations";
import { getErrorTypeEnum } from "../../../api/apiHelper";
import FormContext from "../../../context/FormContext";
import { LifeSupportSection } from "../FormComponents/LifeSupportSection";
import { DisclaimerSection } from "./Disclaimer";
import { State } from "src/components/Premises";

interface IClaimFormContentProps extends IFormFunctionProps {
  dialogValues: ClaimDialogValues;
}

const ClaimFormContent = (props: IClaimFormContentProps) => {
  const { dialogValues } = props;
  const state =
    dialogValues.currentClaimTarget?.claimableJourney?.property_details
      ?.site_details?.state;
  const [savingBlock, setSavingBlock] = useState(false);
  const { setDisplayingAlertBanner } = useContext(FormContext);
  const navigate = useNavigate();
  const { setRequestDetailsLoaded } = useContext(FormContext);

  useEffect(() => {
    setRequestDetailsLoaded(true);
  }, []);

  return (
    <>
      <Formik<ClaimFormValues>
        initialValues={{ ...claimFormInitialValues, ...dialogValues }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={claimFormValidationSchema}
        validate={(values) => {
          let allErrors: any = {};
          try {
            claimFormValidationSchema.validateSync(values, {
              abortEarly: false,
              context: { state },
            });
            setDisplayingAlertBanner(null);
          } catch (e) {
            allErrors = { ...yupToFormErrors(e) };
            setDisplayingAlertBanner(ErrorTypeEnum.OCP_FIELD_VALIDATION);
          }
          return allErrors;
        }}
        onSubmit={async (values) => {
          setSavingBlock(true);
          const formData = claimValuesToRestMapper(
            dialogValues.requestId,
            values,
            dialogValues.requestingCompany?.msaNumber,
            dialogValues.currentClaimTarget?.claimableJourney?.journey_id,
            dialogValues.currentClaimTarget?.claimableJourney
              ?.distributor_application_id,
            dialogValues.projectId,
            state
          );
          try {
            const response = await apiClient.post(`/claim`, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });
            setSavingBlock(false);
            if (response.status) {
              navigate("/", {
                state: {
                  submitted: true,
                  journeyId:
                    dialogValues.currentClaimTarget?.claimableJourney?.journey_id?.toString() ||
                    dialogValues.currentClaimTarget?.claimableJourney?.distributor_application_id?.toString(),
                  snackbarMessage: "Claim request lodged",
                },
              });
            }
          } catch (e) {
            console.log(`Failed to lodge claim: ${e}`);
            setDisplayingAlertBanner(getErrorTypeEnum(e) ?? null);
            setSavingBlock(false);
          }
        }}
      >
        {({ submitForm, values, setFieldValue }) => (
          <Card padding="small" outline borderRadius="medium">
            {/* Form + Button container */}
            <Stack
              space={{
                xs: "xxlarge",
                md: "xxxlarge",
              }}
            >
              {/* Form container */}
              <Stack
                space={{
                  xs: "xlarge",
                  md: "xxlarge",
                }}
              >
                <Heading variant="h3">Claim request</Heading>

                {/* Responsibility for account */}
                <Columns
                  collapseBelow="md"
                  space={{
                    xs: "none",
                    md: "xlarge",
                  }}
                  alignY="stretch"
                >
                  {/* Fields */}
                  <Column width="2/3">
                    <Stack
                      space={{
                        xs: "large",
                        md: "xlarge",
                      }}
                    >
                      {/* Who's responsible? */}
                      <Field id="account-payer" name="electricityAccountPayer">
                        {({
                          field,
                          meta,
                        }: {
                          field: FormikProps<ClaimFormValues>;
                          meta: any;
                        }) => (
                          <RadioGroup
                            {...field}
                            id="electricityAccountPayer"
                            name="electricityAccountPayer"
                            label="Who’s responsible for the electricity account?"
                            error={meta.error}
                            helperText={meta.error}
                            value={values.electricityAccountPayer}
                            onChange={(e) => {
                              Object.keys(accountBlockInitialValues).forEach(
                                (k) => {
                                  setFieldValue(
                                    `${k}`,
                                    accountBlockInitialValues[
                                      k as keyof typeof accountBlockInitialValues
                                    ]
                                  );
                                }
                              );
                              setFieldValue!(
                                "electricityAccountPayer",
                                e.target.value
                              );
                            }}
                          >
                            <Radio
                              id="my-company"
                              label={
                                values.requestingCompany
                                  ? values.requestingCompany?.name
                                  : "My company"
                              }
                              value={AccountPayer.COMPANY}
                              helperText="Will be responsible for any charges until the account is closed."
                            />

                            <Radio
                              id="customer"
                              label="Customer"
                              value={AccountPayer.SOMEONE_ELSE}
                              helperText="Will be responsible for any charges."
                            />
                          </RadioGroup>
                        )}
                      </Field>
                      {/* End Who's responsible? */}

                      {/* Site Supervisor */}
                      <ContactField
                        contactType="SUPERVISOR"
                        name="siteSupervisor"
                        label="Site supervisor"
                      />
                      {/* End Site Supervisor */}

                      {/** Life Support Section */}
                      <LifeSupportSection
                        showPresentation={
                          values.electricityAccountPayer ===
                            AccountPayer.COMPANY && state === State.VIC
                        }
                        fieldProps={{
                          name: "electricityLifeSupport",
                        }}
                      />
                      {/** End Life Support Section */}

                      {/** Disclaimer Section */}
                      <DisclaimerSection
                        showPresentation={
                          values.electricityAccountPayer ===
                          AccountPayer.COMPANY
                        }
                      />
                      {/** End Disclaimer Section */}
                    </Stack>
                  </Column>
                  {/* Fields */}
                  {/* HelperText */}
                  <Column width="1/3" />
                  {/* End HelperText */}
                </Columns>
                {/* End Responsibility for account */}

                {/* Account section */}
                {values.electricityAccountPayer ===
                AccountPayer.SOMEONE_ELSE ? (
                  <Divider />
                ) : null}

                {values.electricityAccountPayer ===
                AccountPayer.SOMEONE_ELSE ? (
                  <ClaimsAccountBlock
                    referenceNumber={dialogValues.requestId}
                    accountPayer={values.electricityAccountPayer}
                    msaNumber={dialogValues.requestingCompany?.msaNumber}
                    projectId={dialogValues.projectId}
                    siteSupervisorId={values.siteSupervisor}
                    dbApplicationId={
                      dialogValues.currentClaimTarget?.claimableJourney
                        ?.distributor_application_id
                    }
                    journeyId={
                      dialogValues.currentClaimTarget?.claimableJourney
                        ?.journey_id
                    }
                  />
                ) : null}
                {/* End Account section */}
              </Stack>
              {/* End Form container */}

              {/* Actions */}
              {values.electricityAccountPayer !== AccountPayer.SOMEONE_ELSE ||
              values.accountHolderDetailsType === "pdf" ? (
                <Button onClick={submitForm} spinner={savingBlock}>
                  Lodge claim
                </Button>
              ) : null}
              {/* End Actions */}
            </Stack>
            {/* End Form + Button container */}
          </Card>
        )}
      </Formik>
    </>
  );
};

export default ClaimFormContent;
